import { FC } from 'react'
import { addDisplayName, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { createUseStyles } from 'react-jss'

import { Span } from './Text'
import Icon, { IconName } from './Icon'

type ButtonType =
  | 'fullWidth'
  | 'wrapped'
  | 'textWrapped'
  | 'image16'
  | 'image24'

const buttonStyles: Record<ButtonType, string> = {
  fullWidth: cx(
    'bg-rodeo-grey/96',
    'text-white',
    'w-full',
    'h-16',
    'rounded-sm',
    'text-center',
    'lowercase'
  ),
  wrapped: cx(
    'bg-rodeo-grey/96',
    'text-white',
    'w-fit',
    'h-12',
    'rounded-sm',
    'text-center',
    'lowercase'
  ),
  textWrapped: cx(
    'text-white',
    'w-fit',
    'text-center',
    'text-rodeo-secondary1',
    'lowercase'
  ),
  image16: cx('h-4', 'w-4'),
  image24: cx('h-6', 'w-6'),
}

const styles = {
  icon: 'inline',
  iconLeft: 'mr-2',
  iconRight: 'ml-2',
}

const disabledStyle = 'opacity-24'

const useStyles = createUseStyles({
  textDisabled: {
    color: 'rgba(255, 255, 255, 0.24)',
  },
})

interface Props {
  text?: string
  onClick: () => void
  disabled?: boolean
  type?: ButtonType
  className?: string
  icon?: IconName
  iconClassName?: string
  showIconAfterText?: boolean
}

export const Button: FC<Props> = flowMax(
  addDisplayName('Button'),
  addProps(({ type, icon, iconClassName, showIconAfterText }) => ({
    type: type ?? 'wrapped',
    isImage: type === 'image16' || type === 'image24',
    icon: icon && (
      <Icon
        name={icon}
        className={cx(
          styles.icon,
          showIconAfterText ? styles.iconRight : styles.iconLeft,
          iconClassName
        )}
      />
    ),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    type,
    text,
    isImage,
    onClick,
    disabled,
    className,
    showIconAfterText,
    icon,
    classes,
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cx(buttonStyles[type], disabled && disabledStyle, className)}
      style={{ paddingInline: isImage ? 0 : 20 }}
    >
      {!isImage && (
        <Span type="button" className={cx(disabled && classes.textDisabled)}>
          {!showIconAfterText && icon} {text}
          {showIconAfterText && icon}
        </Span>
      )}

      {isImage && icon}
    </button>
  )
)
